import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ResourceManager, ResourceManagerService } from '@barthauer/abc/core/resources';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserService } from '@barthauer/aptis-authentication';
export interface BulkTranslationItem {
  field: string;
  key: string;
  params?: Record<string, any>;
}
@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private translations: Record<string, any> = {};
  private defaultLang = 'en';

  constructor(
    private http: HttpClient,
    private userService: UserService,
  ) {}
 private languageParam = sessionStorage.getItem('fixedCulture') ?? document.documentElement.lang;

  private localStorageSubject = new BehaviorSubject<string>(this.languageParam);
  localStorageChanges$ = this.localStorageSubject.asObservable();

  setValue(key: string, value: string): void {
    if (!value) return
   
    ResourceManagerService.setFixedCulture(value);
    ResourceManager.changeLanguage(value);

    this.localStorageSubject.next(value);
  }
  async init() {
    const profileId = sessionStorage.getItem('profileId');

    let language = environment.defaultLanguage;

    if (profileId) {
      try {
        language = await this.userService.getAccountLanguage();

        if (!language) {
          language = environment.defaultLanguage;
          await this.userService.setAccountLanguage(language);
        }
      } catch {
        language = environment.defaultLanguage;
      }
    }

    await this.setValue('fixedCulture', language);
  }
  changeLanguage(language: string) {
    this.localStorageSubject.next(language);
  }

  getValue(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  // Load language translations from JSON file
  async loadLanguage(lang: string): Promise<void> {
    if (this.translations[lang]) return;
    const data = await this.http.get(`assets/resources/ubt-geods.${lang}.json`).toPromise();
    this.translations[lang] = data[lang];
  }

  // Translate a key to the specified language
  public async translate(key: string, lang: string, params?: Record<string, string>): Promise<string> {
    await this.loadLanguage(lang);
    let value = this.translations[lang][key];

    // Fallback
    if (!value && lang !== this.defaultLang) {
      await this.loadLanguage(this.defaultLang);
      value = this.getNestedValue(this.translations[this.defaultLang], key);
    }

    if (!value) return key;

    return this.replaceParams(value, params);
  }

  public async translateBulk(items: BulkTranslationItem[], lang: string): Promise<Record<string, string>> {
    await this.loadLanguage(lang);

    const result: Record<string, string> = {};

    for (const item of items) {
      let value = this.translations[lang][item.key];

      // fallback
      if (!value && lang !== this.defaultLang) {
        await this.loadLanguage(this.defaultLang);
        value = this.translations[this.defaultLang][item.key];
      }

      if (!value) {
        result[item.field] = item.key;
        continue;
      }
      result[item.field] = this.replaceParams(value, item.params);
    }

    return result;
  }

  // Get nested value from object using dot notation
  private getNestedValue(obj: any, key: string): string {
    return key.split('.').reduce((o, i) => o?.[i], obj);
  }

  // Replace placeholders in a string with actual values
  private replaceParams(text: string, params?: Record<string, string>): string {
    if (!params) return text;

    return Object.keys(params).reduce((acc, key) => {
      return acc.replace(new RegExp(`\\{${key}\\}`, 'g'), params[key]);
    }, text);
  }
}
